import React, { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag, Tooltip } from 'antd';
import TableIndex from '@shared/table/TableIndex';
import type { TableService, SupportRule } from '@shared/table/TableIndex';
import { formatDateTime } from '@utils/dateTime';
import { MultipleValue } from '@shared/table/Rules/component';
import FullDayRangePicker from '@components/shared/FullDayRangePicker';

const LOGGABLE_TYPE_I18N_KEY: Record<string, string> = {
  EcAccount: 'models.ec_account._',
  Merchant: 'models.merchant._',
};

const preStyle: React.CSSProperties = {
  background: '#f5f5f5',
  padding: '8px',
  borderRadius: '4px',
  margin: 0,
  fontSize: '11px',
  overflow: 'auto',
  maxHeight: '300px',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const labelStyle: React.CSSProperties = { fontSize: '11px', color: '#999', marginBottom: '4px' };

interface ThirdPartyApiLogRow {
  id: string;
  created_at?: string;
  request_method?: string;
  loggable_type?: string;
  request_path?: string;
  response_http_status?: number;
  duration_ms?: number;
  request?: unknown;
  response?: unknown;
  [key: string]: unknown;
}

interface ExpandedRowProps {
  data: ThirdPartyApiLogRow;
}

const ExpandedRow: React.FC<ExpandedRowProps> = ({ data }) => {
  const { t } = useTranslation();

  const formatBody = (body: unknown): string => {
    if (!body) return '-';
    if (typeof body !== 'string') return JSON.stringify(body, null, 2);
    try {
      return JSON.stringify(JSON.parse(body), null, 2);
    } catch {
      return body;
    }
  };

  return (
    <div style={{ padding: '12px 16px', background: '#fafafa' }}>
      <div style={{ display: 'flex', gap: '16px' }}>
        <div style={{ flex: 1 }}>
          <div style={labelStyle}>{t('common.api_logs.request')}</div>
          <pre style={preStyle}>{formatBody(data.request)}</pre>
        </div>
        <div style={{ flex: 1 }}>
          <div style={labelStyle}>{t('common.api_logs.response')}</div>
          <pre style={preStyle}>{formatBody(data.response)}</pre>
        </div>
      </div>
    </div>
  );
};

interface LazyLoadService {
  fetchList: (params: Record<string, unknown>) => Promise<{ data: Array<{ id: string; display_name: string }> }>;
  [key: string]: unknown;
}

interface ThirdPartyApiLogListProps {
  role?: string;
  service: TableService;
  ecAccountService?: LazyLoadService;
  shippingAccountService?: LazyLoadService;
}

const ThirdPartyApiLogList: React.FC<ThirdPartyApiLogListProps> = ({ role = 'operator', service, ecAccountService, shippingAccountService }) => {
  const { t } = useTranslation();
  const [lastModified, setLastModified] = useState(0);

  const columns = useMemo(() => [
    {
      name: t('common.created_at'),
      selector: (row: ThirdPartyApiLogRow) => formatDateTime(row.created_at as string),
      sortable: true,
      sortFieldName: 'third_party_api_logs.created_at',
      width: '180px',
    },
    {
      name: t('models.third_party_api_log.method'),
      selector: (row: ThirdPartyApiLogRow) => row.request_method,
      width: '100px',
      cell: (row: ThirdPartyApiLogRow) => <Tag>{row.request_method}</Tag>,
    },
    {
      name: t('models.third_party_api_log.loggable'),
      selector: (row: ThirdPartyApiLogRow) => row.loggable_type,
      width: '130px',
      cell: (row: ThirdPartyApiLogRow) => (
        <Tag color="blue">
          {t(LOGGABLE_TYPE_I18N_KEY[row.loggable_type || ''] || row.loggable_type || '', { defaultValue: row.loggable_type }) as ReactNode}
        </Tag>
      ),
    },
    {
      name: t('models.third_party_api_log.path'),
      selector: (row: ThirdPartyApiLogRow) => row.request_path,
      cell: (row: ThirdPartyApiLogRow) => (
        <Tooltip title={row.request_path} placement="topLeft">
          <div style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            cursor: 'default',
            fontSize: 12,
          }}>
            {row.request_path}
          </div>
        </Tooltip>
      ),
    },
    {
      name: t('models.third_party_api_log.status'),
      selector: (row: ThirdPartyApiLogRow) => row.response_http_status,
      width: '100px',
      cell: (row: ThirdPartyApiLogRow) => {
        const status = row.response_http_status;
        let color = 'default';
        if (status !== undefined && status >= 200 && status < 300) color = 'success';
        if (status !== undefined && status >= 400 && status < 500) color = 'warning';
        if (status !== undefined && status >= 500) color = 'error';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      name: t('models.third_party_api_log.duration'),
      selector: (row: ThirdPartyApiLogRow) => `${row.duration_ms?.toFixed(2)} ms`,
      width: '120px',
    },
  ], [t]);

  const supportRules = useMemo(() => {
    const rules: SupportRule[] = [
      {
        label: t('common.created_at'),
        component: FullDayRangePicker,
        inputName: 'third_party_api_logs.created_at',
        defaultPinned: true,
      },
    ];

    if (ecAccountService) {
      rules.push({
        label: t('models.third_party_api_log.ec_account'),
        component: MultipleValue,
        inputName: 'third_party_api_logs.ec_account_id',
        lazyLoad: {
          action: (params: Record<string, unknown>) => ecAccountService.fetchList(params),
          transform: ({ data }: { data: Array<{ id: string; display_name: string }> }) => data.map(item => ({ label: item.display_name, value: item.id })),
        },
      });
    }

    if (shippingAccountService) {
      rules.push({
        label: t('models.third_party_api_log.shipping_account'),
        component: MultipleValue,
        inputName: 'third_party_api_logs.shipping_account_id',
        lazyLoad: {
          action: (params: Record<string, unknown>) => shippingAccountService.fetchList(params),
          transform: ({ data }: { data: Array<{ id: string; display_name: string }> }) => data.map(item => ({ label: item.display_name, value: item.id })),
        },
      });
    }

    return rules;
  }, [t, ecAccountService, shippingAccountService]);

  return (
    <TableIndex
      columns={columns}
      searchable={false}
      tableId={`${role}ThirdPartyApiLogsTable`}
      defaultQuery={{
        sorting_field: 'third_party_api_logs.created_at',
        sorting_direction: 'desc',
      }}
      supportRules={supportRules}
      lastModified={lastModified}
      setLastModified={setLastModified}
      service={service}
      expandableRows
      expandableRowsComponent={ExpandedRow as React.ComponentType<any>}
    />
  );
};

export default ThirdPartyApiLogList;
